// Auth helpers for the bearer-token OIDC flow.
//
// The backend contract (cookie-free; SPA owns its own token storage):
//
//   1. Frontend GETs /api/auth/login?return_to=/cases/abc. The response
//      is JSON `{ url: "https://accounts.google.com/..." }`. The SPA
//      navigates the browser to that URL.
//
//   2. Google redirects to /api/auth/callback?code=...&state=...
//      The API exchanges the code, mints access + refresh tokens, and
//      302s to FRONTEND_BASE_URL + "/login/complete#access_token=…&
//      refresh_token=…&return_to=…".
//
//   3. The /login/complete route parses the fragment, stores both
//      tokens in lib/tokens.ts, and navigates to return_to.
//
//   4. halFetch attaches `Authorization: Bearer <access_token>` on
//      every subsequent request and transparently refreshes on 401.
//
//   5. Logout posts the refresh token to /api/auth/logout (which
//      revokes the server-side row), then clears local storage so the
//      next request 401s and the route guard redirects to /login.
//
// Exports:
//
//   - currentUserQueryOptions: TanStack Query options for /me.
//   - beginLogin: kicks off the Google dance.
//   - useLogoutMutation: logs out + clears tokens + drops the /me cache.

import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { clearDrafts } from "~/lib/draftStore";
import { halFetch, type HalResource } from "~/lib/hal";
import { clearTokens, getRefreshToken } from "~/lib/tokens";
import { unregisterVoice } from "~/lib/voice/registration";

// ── Types ─────────────────────────────────────────────────────────────

/**
 * The user shape returned by /api/auth/me. Matches the Go User model's
 * HAL-aware MarshalJSON. Only the fields we actually read are declared;
 * additional fields the backend might add are still carried on the
 * HalResource envelope and typed as unknown.
 */
export type AuthUser = HalResource<{
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  type: string;
  /**
   * Firm-task role used by the priority queue. "" when the user has
   * no firm role assigned (their queue will be empty). Distinct from
   * `type`, which is the auth tier.
   */
  role: string;
}>;

// ── Query ─────────────────────────────────────────────────────────────

/**
 * Query key for the /me query. Exported so callers can invalidate it
 * (e.g., after a profile update mutation) without reimplementing the
 * key string.
 */
export const AUTH_ME_QUERY_KEY = ["auth", "me"] as const;

/**
 * Shared query options for fetching the current user. Pass this to
 * both `useSuspenseQuery`/`useQuery` in components and
 * `queryClient.ensureQueryData` in route loaders — they'll hit the
 * same cache entry.
 *
 * staleTime is longer than the default 30s because the user rarely
 * changes and a fresh lookup on every route change would be wasteful.
 * The real invalidation trigger is logout, which explicitly removes
 * the cache entry.
 */
export const currentUserQueryOptions = queryOptions<AuthUser>({
  queryKey: AUTH_ME_QUERY_KEY,
  queryFn: ({ signal }) =>
    halFetch<AuthUser>("/api/auth/me", { signal }),
  staleTime: 5 * 60 * 1000, // 5 minutes
});

// ── Login redirect helper ─────────────────────────────────────────────

interface LoginURLResponse {
  url: string;
}

/**
 * Start the Google OIDC flow. Fetches the authorization URL from the
 * API (which generates and signs the state param) and then navigates
 * the browser to it.
 *
 * After the user completes the Google dance, the API 302s to
 * /login/complete with the access + refresh tokens in the URL fragment;
 * that route stores the tokens and navigates to returnTo.
 *
 * Throws HalError if the login endpoint itself fails (unusual — it
 * only requires the state secret and OIDC config to be wired).
 *
 * @param returnTo  Relative path to deep-link the user back to after
 *                  login. Must start with "/" and must not be
 *                  scheme-relative — the backend validates this and
 *                  rejects anything else as a 400.
 */
export async function beginLogin(returnTo: string): Promise<void> {
  const params = new URLSearchParams();
  if (returnTo.length > 0) {
    params.set("return_to", returnTo);
  }
  const path = params.toString().length > 0
    ? `/api/auth/login?${params.toString()}`
    : "/api/auth/login";

  const resp = await halFetch<LoginURLResponse>(path);

  // Full-page navigation, not a SPA transition. The whole point is
  // to leave our origin and go to accounts.google.com.
  window.location.href = resp.url;
}

// ── Logout mutation ───────────────────────────────────────────────────

/**
 * POST /api/auth/logout mutation. The API revokes the server-side
 * refresh-token row when the plaintext refresh token is supplied in
 * the body. We always clear local tokens on settlement (success or
 * error) — the user's intent is "be logged out," so a server-side
 * revocation failure should not leave them with a live local session.
 *
 *   const logout = useLogoutMutation();
 *   const onClick = () => {
 *     logout.mutate(undefined, {
 *       onSettled: () => router.navigate({ to: "/login" }),
 *     });
 *   };
 */
export function useLogoutMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      // Signing off takes the browser off the phone FIRST, while the access
      // token that authorizes it is still on hand (TMF-346). Leaving it
      // registered would keep offering the firm's callers a browser whose rep
      // has walked away, until the window lapsed on its own. Best effort: the
      // rep's intent is to be logged out, and a failure here must not hold that
      // up — the window expires within minutes regardless.
      await unregisterVoice().catch(() => undefined);

      const refresh = getRefreshToken();
      // Even with no refresh token on hand, hit the endpoint with an
      // empty body — the API returns 204 unconditionally per RFC 7009
      // §2.2 ("the server SHOULD ignore the error").
      return halFetch<null>("/api/auth/logout", {
        method: "POST",
        body: { refresh_token: refresh ?? "" },
      });
    },
    onSettled: () => {
      clearTokens();
      // Local drafts are PHI; signing off a shared workstation must not
      // leave a rep's call notes for the next person.
      clearDrafts();
      queryClient.removeQueries({ queryKey: AUTH_ME_QUERY_KEY });
    },
  });
}
